import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Formik, Form, getIn } from 'formik'
import * as yup from 'yup'
import Button from '../../atoms/Button/Button'
import UploadLoghi from '../../organisms/UploadLoghi/UploadLoghi'
import FormField from '../../atoms/FormField/FormField'
import FormInput from '../../atoms/FormInput/FormInput'
import PantoneSelect from '../../atoms/PantoneSelect/PantoneSelect'
import useTrans from '../../../hooks/useTrans'
import { api } from '../../../utils'
import { PANTONI } from '../../../editor-bozze/utils/endpoints'
import { useRouter } from 'next/router'

const clamp = (val, min, max) => {
  const n = Number(val)
  if (Number.isNaN(n)) return ''
  return Math.min(Math.max(n, min), max)
}

const ConfigurazioneProdottoConferma = ({
  props,
  onConfirm,
  onCreaOfferta,
  onSaveLater,
  posizioni,
  handleChange,
  stessoLogo = false,
  isCart = false,
  setVisible,
  articoliPreventivo,
  variant,
}) => {
  const t = useTrans()
  const router = useRouter()

  const [loghi, setLoghi] = useState(
    posizioni?.map((pos) => ({
      url: pos.logo_file,
      tracciatura: pos.tracciatura_logo,
    }))
  )
  const [note, setNote] = useState(posizioni?.map((pos) => pos.descrizione_posizione || '') || [])
  const [caricaInSeguito, setCaricaInSeguito] = useState(false)
  const [conferma, setConferma] = useState(false)
  const [pantoni, setPantoni] = useState([])

  const meta = useMemo(() => {
    return (posizioni || []).map((posizione) => {
      const personalizzazione = !isCart
        ? posizione?.colori?.personalizzazioni_objects?.[0]
        : posizione?.personalizzazione

      const larghezzaMax = Number(personalizzazione?.dimensione_larghezza)
      const altezzaMax = Number(personalizzazione?.dimensione_altezza)
      const pantoniConsentiti = Array.isArray(personalizzazione?.tecnica?.editor_pantoni_consentiti) && !personalizzazione?.tecnica?.editor_modalita_colori_fissi
        ? personalizzazione?.tecnica.editor_pantoni_consentiti.filter(Boolean)
        : []

      const pantoni =
        personalizzazione?.accetta_pantone && personalizzazione?.numero_colori
          ? {
              accettaPantone: personalizzazione.accetta_pantone,
              numeroColori: personalizzazione.numero_colori,
              // Se la tecnica limita i pantoni, la select deve mostrare solo quelli consentiti.
              pantoniConsentiti,
            }
          : null

      return { larghezzaMax, altezzaMax, pantoni }
    })
  }, [posizioni, isCart])

  useEffect(() => {
    const hasFiles = loghi?.filter((l) => l.file || l.url).length
    setConferma(stessoLogo ? hasFiles > 0 : hasFiles > 1)
  }, [loghi, stessoLogo])

  useEffect(() => {
    let mounted = true

    const loadPantoni = async () => {
      try {
        const response = await api.get(PANTONI, {
          params: { lang: router.locale },
        })
        const data = response?.data || []
        if (mounted) setPantoni(data)
      } catch (error) {
        if (mounted) setPantoni([])
      }
    }

    loadPantoni()
    return () => {
      mounted = false
    }
  }, [router.locale])

  const initialValues = useMemo(() => {
    return {
      posizioni: (posizioni || []).map((p, idx) => {
        const pantLen = meta[idx]?.pantoni?.numeroColori || 0
        const existingPantoni = Array.isArray(p?.pantoni_codici) ? p.pantoni_codici : []
        return {
          selectedLarghezza: p?.selectedLarghezza ?? '',
          selectedAltezza: p?.selectedAltezza ?? '',
          pantoni_codici: Array.from({ length: pantLen }, (_, i) => existingPantoni[i] || ''),
          descrizione_posizione: p?.descrizione_posizione || '',
        }
      }),
    }
  }, [posizioni, meta])

  const validationSchema = useMemo(() => {
    const dimensionRule = (getMax) => {
      return yup.mixed().test('dim', function (value) {
        const idxMatch = this.path.match(/\[(\d+)\]/)
        const idx = idxMatch ? Number(idxMatch[1]) : 0
        const max = Number(getMax(idx))

        if (max === 0) {
          return true // se la dimenzione è 0 non è richiesto il campo
        }

        if (value === '' || value === undefined || value === null) {
          return this.createError({ message: t('Campo obbligatorio') })
        }

        const num = Number(value)
        if (num <= 0 || num > max) {
          return this.createError({ message: t('Solo numeri positivi') })
        }

        return true
      })
    }

    return yup.object().shape({
      posizioni: yup.array().of(
        yup.object().shape({
          selectedLarghezza: dimensionRule((idx) => meta[idx]?.larghezzaMax),
          selectedAltezza: dimensionRule((idx) => meta[idx]?.altezzaMax),
          pantoni_codici: yup
            .array()
            .of(yup.string().required(t('Questo campo è obbligatorio')).max(20)),
        })
      ),
    })
  }, [meta])

  const actionRef = useRef(null)

  const isButtonsDisabled = (isValid) => {
    return !(isValid && (caricaInSeguito || conferma))
  }

  return (
    <Formik
      enableReinitialize={isCart}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        values.posizioni?.forEach((p, idx) => {
          handleChange?.(idx, 'selectedLarghezza', p.selectedLarghezza)
          handleChange?.(idx, 'selectedAltezza', p.selectedAltezza)
          handleChange?.(idx, 'pantoni_codici', p.pantoni_codici)
          handleChange?.(idx, 'descrizione_posizione', note[idx])
        })

        if (actionRef.current === 'add_to_cart') {
          onConfirm?.(loghi)
        } else if (actionRef.current === 'crea_offerta') {
          onCreaOfferta?.(loghi)
        } else if (actionRef.current === 'save_later') {
          onSaveLater?.(loghi)
        }
      }}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched, handleBlur, isValid }) => (
        <Form>
          <div className="caricamento-loghi">
            {!caricaInSeguito && (
              <div className="caricamento-loghi__positions">
                <UploadLoghi
                  note={note}
                  setNote={setNote}
                  posizioni={stessoLogo ? [0] : [0, 1]}
                  loghi={loghi}
                  setLoghi={setLoghi}
                  posizioniList={posizioni}
                  articoliPreventivo={articoliPreventivo}
                />
              </div>
            )}

            <p className="newmodal__title">{t('Inserimento dimensioni e pantoni')}</p>
            <div className={'configurazione-prodotto-conferma__list'}>
              {posizioni?.map((posizione, index) => {
                const m = meta[index]
                const pantoniList = m?.pantoni
                const pantoniSelectList =
                  pantoniList?.pantoniConsentiti?.length > 0
                    ? pantoniList.pantoniConsentiti
                    : pantoni

                const widthPath = `posizioni[${index}].selectedLarghezza`
                const heightPath = `posizioni[${index}].selectedAltezza`
                const pantonePath = `posizioni[${index}].pantoni_codici`

                const widthError = getIn(errors, widthPath)
                const widthTouched = getIn(touched, widthPath)
                const heightError = getIn(errors, heightPath)
                const heightTouched = getIn(touched, heightPath)
                return (
                  <div
                    className="configurazione-prodotto__item configurazione-prodotto__item__conferma"
                    key={index}
                  >
                    <div className={'configurazione-prodotto-conferma__head'}>
                      <p className="posizione__title">{posizione.nome}</p>
                    </div>
                    <div className={'configurazione-prodotto-conferma__container'}>
                      <div className="configurazione-prodotto-conferma__fields">
                        <FormField
                          id="configurazione_prodotto_conferma_larghezza"
                          status={widthTouched && widthError ? 'error' : undefined}
                          errorMessage={widthError ? widthError : undefined}
                          label={`${t('Larghezza')} (CM)`}
                          subtitle={`${t('Max')} ${m?.larghezzaMax} cm`}
                        >
                          <FormInput
                            id="configurazione_prodotto_conferma_larghezza"
                            value={values.posizioni[index]?.selectedLarghezza}
                            placeholder={`${t('Max')} ${m?.larghezzaMax}`}
                            type="number"
                            onChange={(e) => {
                              const newVal = clamp(e.target.value, 0, m?.larghezzaMax)
                              setFieldValue(widthPath, newVal)
                              handleChange?.(index, 'selectedLarghezza', newVal)
                            }}
                            onBlur={handleBlur(widthPath)}
                            max={m?.larghezzaMax || 0}
                          />
                        </FormField>

                        {m?.altezzaMax !== 0 && (
                          <FormField
                            id="configurazione_prodotto_conferma_altezza"
                            status={heightTouched && heightError ? 'error' : undefined}
                            errorMessage={heightError ? heightError : undefined}
                            label={`${t('Altezza')} (CM)`}
                            subtitle={`${t('Max')} ${m?.altezzaMax} cm`}
                          >
                            <FormInput
                              id="configurazione_prodotto_conferma_altezza"
                              value={values.posizioni[index]?.selectedAltezza}
                              placeholder={`${t('Max')} ${m?.altezzaMax}`}
                              type="number"
                              onChange={(e) => {
                                const newVal = clamp(e.target.value, 0, m?.altezzaMax)
                                setFieldValue(heightPath, newVal)
                                handleChange?.(index, 'selectedAltezza', newVal)
                              }}
                              onBlur={handleBlur(heightPath)}
                              max={m?.altezzaMax || 0}
                            />
                          </FormField>
                        )}
                      </div>

                      {pantoniList && (
                        <div className="configurazione-prodotto-conferma__separator" />
                      )}

                      {pantoniList && (
                        <div className="configurazione-prodotto-conferma__pantoni">
                          <div className="configurazione-prodotto-conferma__fieldsPantone">
                            {Array.from({ length: pantoniList.numeroColori }).map((_, i) => {
                              const pPath = `${pantonePath}[${i}]`
                              const pError = getIn(errors, pPath)
                              const pTouched = getIn(touched, pPath)
                              return (
                                <div
                                  className="configurazione__stampa__field"
                                  key={`configurazione-stampa-pantone-${index}-${i}`}
                                >
                                  <FormField
                                    id={`configurazione_pantone_${index}_${i}`}
                                    label={`${t('Pantone {0}', [i + 1])} *`}
                                    status={pError && pTouched ? 'error' : undefined}
                                    errorMessage={pError ? pError : null}
                                  >
                                    <PantoneSelect
                                      id={`configurazione_pantone_${index}_${i}`}
                                      showSwatchInValue
                                      fixedMenu
                                      name={pPath}
                                      value={values.posizioni[index]?.pantoni_codici?.[i] || ''}
                                      valueMode="code"
                                      placeholder={t('Seleziona colore')}
                                      initialPantoni={pantoniSelectList}
                                      onBlur={handleBlur(pPath)}
                                      onChange={async (e) => {
                                        const nextValue = e.target.value || ''
                                        const updatedPantoni = [
                                          ...(values.posizioni[index]?.pantoni_codici || []),
                                        ]
                                        updatedPantoni[i] = nextValue
                                        await setFieldValue(pPath, nextValue)
                                        setFieldTouched(pPath, true)
                                        handleChange?.(index, 'pantoni_codici', updatedPantoni)
                                      }}
                                    />
                                  </FormField>
                                </div>
                              )
                            })}
                          </div>
                          <p className="configurazione-prodotto-conferma__pantoni-disclaimer">
                            *{' '}
                            {t(
                              'Qualora il Pantone desiderato non fosse disponibile, selezionare la tonalità più simile e indicare il codice originale nel campo note'
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {!isCart && (
              <div className="caricamento-loghi__confirm">
                <Button
                  type="submit"
                  onClick={() => {
                    actionRef.current = 'add_to_cart'
                  }}
                  label={
                    variant === 'personalizzazione'
                      ? t('Aggiorna il carrello')
                      : t('Aggiungi al carrello')
                  }
                  variant="primary"
                  disabled={isButtonsDisabled(isValid)}
                />
                <Button
                  type="submit"
                  onClick={() => {
                    actionRef.current = 'crea_offerta'
                  }}
                  label={t('Crea offerta')}
                  variant="secondary"
                  disabled={isButtonsDisabled(isValid)}
                />
                {!!onSaveLater && (
                  <Button
                    type="submit"
                    onClick={() => {
                      actionRef.current = 'save_later'
                    }}
                    label={t('Salva per dopo')}
                    variant="secondary"
                    disabled={isButtonsDisabled(isValid)}
                  />
                )}
              </div>
            )}

            {isCart && (
              <div className="caricamento-loghi__confirm">
                <Button
                  type="submit"
                  onClick={() => {
                    actionRef.current = 'add_to_cart'
                  }}
                  variant="primary"
                  size="medium"
                  label={t('Carica')}
                  disabled={isButtonsDisabled(isValid)}
                />
                <Button
                  type="button"
                  onClick={() => setVisible?.(false)}
                  variant="secondary"
                  size="medium"
                  label={t('Chiudi')}
                />
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default ConfigurazioneProdottoConferma
